import axios, { AxiosInstance } from 'axios';
import { unifiedStorage } from '@/lib/unified-storage';

const QUEUE_API_URL = (import.meta.env.VITE_QUEUE_API || 'http://localhost:3001') + '/api/v1';

export interface CatalogScheduledQueue {
  queueId: string;
  scheduledTime: string; // HH:mm format
}

export interface Catalog {
  _id: string;
  eventId: string;
  highlightQueueId?: string;
  scheduledQueues: CatalogScheduledQueue[];
  accessToken: string;
  isActive: boolean;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCatalogDto {
  highlightQueueId?: string;
  scheduledQueues?: CatalogScheduledQueue[];
  isActive?: boolean;
  accessToken?: string;
}

export interface UpdateCatalogDto extends CreateCatalogDto {}

export interface PublicQueueInfo {
  queueId: string;
  name: string;
  description: string;
  status: string; // 'active' | 'paused'
  currentSize: number;
  estimatedWait: number; // em minutos
  maxSize: number | null;
  isFull: boolean;
}

export interface PublicScheduledQueueInfo extends PublicQueueInfo {
  scheduledTime: string; // HH:mm
}

export interface PublicCatalogResponse {
  eventId: string;
  highlight: PublicQueueInfo | null;
  scheduledQueues: PublicScheduledQueueInfo[];
}

class CatalogApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: QUEUE_API_URL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.api.interceptors.request.use((config) => {
      const tokens = unifiedStorage.getTokens();
      const eventId = unifiedStorage.getEventId();
      if (tokens?.accessToken) config.headers.Authorization = `Bearer ${tokens.accessToken}`;
      if (eventId) config.headers['X-Event-Id'] = eventId;
      return config;
    });
  }

  async getCatalog(): Promise<Catalog | null> {
    try {
      const response = await this.api.get<Catalog>('/catalog');
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  }

  async createCatalog(dto: CreateCatalogDto): Promise<Catalog> {
    const response = await this.api.post<Catalog>('/catalog', dto);
    return response.data;
  }

  async updateCatalog(dto: UpdateCatalogDto): Promise<Catalog> {
    const response = await this.api.put<Catalog>('/catalog', dto);
    return response.data;
  }

  async regenerateToken(customToken?: string): Promise<Catalog> {
    const body = customToken ? { accessToken: customToken } : {};
    const response = await this.api.post<Catalog>('/catalog/regenerate-token', body);
    return response.data;
  }

  async getPublicCatalog(accessToken: string): Promise<PublicCatalogResponse | null> {
    try {
      const response = await axios.get<PublicCatalogResponse>(
        `${QUEUE_API_URL}/catalog/public/${accessToken}`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      throw error;
    }
  }
}

export const catalogApiService = new CatalogApiService();
